import React, { useState } from 'react';
import api from '../utils/api';

export default function PandaDocModal({ host, programs, onClose, onSent }) {
  const emailOptions = (host.contact_email || '')
    .split(',').map(e => e.trim()).filter(Boolean);

  const [signerEmail, setSignerEmail] = useState(emailOptions[0] || '');
  const [programCode, setProgramCode] = useState(programs[0]?.code || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [opened, setOpened] = useState(false);

  async function handleOpen() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`/hosts/${host.id}/send-agreement`, {
        programCode,
        signerEmail,
      });
      window.open(res.data.editUrl, '_blank');
      setOpened(true);
      onSent?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to prepare document');
    } finally {
      setLoading(false);
    }
  }

  if (opened) {
    return (
      <Modal onClose={onClose}>
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>↗</div>
          <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Opened in PandaDoc</h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
            The pre-filled agreement opened in a new tab.<br />
            Review it and click <strong>Send Document</strong> inside PandaDoc to deliver it to {signerEmail}.
          </p>
          <button onClick={onClose} className="btn btn-primary">Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 18 }}>
        Send Site Agreement — {host.org_name}
      </h2>

      {emailOptions.length > 1 ? (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Send to</label>
          <select value={signerEmail} onChange={e => setSignerEmail(e.target.value)} className="input">
            {emailOptions.map(email => <option key={email} value={email}>{email}</option>)}
          </select>
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Recipient</label>
          <div style={{ fontSize: 14, color: 'var(--ink-2)', padding: '6px 0' }}>
            {emailOptions[0] || <span style={{ color: '#dc2626' }}>No contact email set</span>}
          </div>
        </div>
      )}

      {programs.length > 1 ? (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Program</label>
          <select value={programCode} onChange={e => setProgramCode(e.target.value)} className="input">
            {programs.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Program</label>
          <div style={{ fontSize: 14, color: 'var(--ink-2)', padding: '6px 0' }}>{programs[0]?.name || '—'}</div>
        </div>
      )}

      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20, lineHeight: 1.5 }}>
        A pre-filled draft will be created and opened in PandaDoc in a new tab. Click <strong>Send Document</strong> there to deliver it to the host.
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
          padding: '10px 12px', fontSize: 13, color: '#dc2626', marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} disabled={loading} className="btn btn-ghost">Cancel</button>
        <button
          onClick={handleOpen}
          disabled={loading || !signerEmail || !programCode}
          className="btn btn-primary"
        >
          {loading ? 'Preparing…' : 'Open in PandaDoc'}
        </button>
      </div>
    </Modal>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 5,
};

function Modal({ children, onClose }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 480,
        padding: 28, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 14, right: 16, background: 'none',
          border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--ink-3)', lineHeight: 1,
        }}>✕</button>
        {children}
      </div>
    </div>
  );
}
